import React from "react";
import type { ExtendMotionProps } from "@/lib/types/motion";

/**
 * A utility type that combines the props of a given React element type `T`
 * with any additional custom props (`ExtraProps`), excluding ref-related props.
 *
 * This is useful when building polymorphic components or utilities that
 * should support arbitrary React components or HTML tags as the base element,
 * while allowing extra props to be layered on top.
 *
 * @template T - The intrinsic or component type to extract props from.
 * @template ExtraProps - Additional props to merge with the base component props.
 */
export type WithRefProps<
  T extends React.ElementType,
  ExtraProps = unknown,
> = React.ComponentPropsWithoutRef<T> & ExtraProps;

/**
 * A motion-aware version of `WithRefProps` that supports Framer Motion props
 * like `initial`, `animate`, `exit`, `whileHover`, etc.
 *
 * Internally wraps the component props of type `T` with `ExtendMotionProps`,
 * which ensures compatibility with `motion.*` components.
 *
 * Useful when you're building components that should support motion behavior,
 * without losing type safety or needing to manually extend Framer Motion props.
 *
 * @template T - The base element or component type (e.g. 'div', 'button', etc).
 * @template ExtraProps - Any extra props to be merged with the motion-aware props.
 */
export type WithMotionRefProps<
  T extends React.ElementType,
  ExtraProps = unknown,
> = ExtendMotionProps<React.ComponentPropsWithoutRef<T>> & ExtraProps;

/**
 * A utility function that wraps a component with `React.forwardRef`, while
 * providing strong typing support for both the props and the forwarded ref.
 *
 * This is useful when creating a component that needs to forward its ref to
 * a DOM element or another component, especially when building wrapper components.
 *
 * Compared to the typical `forwardRef` usage, this helper makes it easier to
 * define reusable, typed components with polymorphic or generic element types.
 *
 * ?? **Usage Tip:**
 * To get full type safety, you should **almost always** pass either `WithRefProps`
 * or `WithMotionRefProps` as the second type argument. This ensures that your
 * props match the intrinsic element type you're working with, including proper
 * support for `ref`.
 *
 * Example:
 * ```tsx
 * type ButtonElement = 'button';
 *
 * const MyButton = withRef<ButtonElement, WithRefProps<ButtonElement>>((props, ref) => (
 *   <button ref={ref} {...props} />
 * ));
 * ```
 *
 * @template T - The element type to which the ref should be forwarded.
 * @template Props - The full set of props the component accepts.
 * @param render - A render function that accepts props and a forwarded ref.
 * @returns A component wrapped in `React.forwardRef`, typed accordingly.
 */
export function withRef<T extends React.ElementType, Props = unknown>(
  render: React.ForwardRefRenderFunction<
    React.ComponentRef<T>,
    React.PropsWithoutRef<Props>
  >,
) {
  return React.forwardRef(render);
}
